import { useSuspenseQuery } from '@apollo/client';

import { GenericPageError, Head } from '@xing-com/crate-career-hub-common';
import { HeaderNavigation } from '@xing-com/crate-career-hub-header-navigation';
import { LoggedOutFooter } from '@xing-com/crate-career-hub-logged-out-footer';
import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { NoSSRLoggedInSuspense } from '@xing-com/crate-utils-no-ssr';
import { Container, Section } from '@xing-com/layout-primitives';

import { TOPIC_DATA_SEO } from './constants';
import { GetCareerHubTopicPageDocument } from './graphql/career-hub-topic-page-query.gql-types';
import { TopicPageSkeleton } from './skeletons';
import { TopicArticle } from './topic-article';
import { TopicCarousel } from './topic-carousel';
import { TopicPageHeader } from './topic-page-header';

export type TopicPageEntryProps = {
  topicKey: string;
};

export const TopicPageEntryInner: React.FC<TopicPageEntryProps> = ({
  topicKey,
}) => {
  const { data } = useSuspenseQuery(GetCareerHubTopicPageDocument, {
    variables: { topicKey },
  });

  const {
    articles = [],
    evergreenArticleUrn,
    evergreenTitle = '',
    introduction = [],
    title = '',
  } = data?.careerHubTopicPage || {};

  return (
    <>
      <HeaderNavigation {...{ title }} />
      <TopicPageHeader {...{ introduction, evergreenTitle }} />
      <TopicCarousel {...{ articles }} />
      {evergreenArticleUrn && <TopicArticle globalId={evergreenArticleUrn} />}
    </>
  );
};

export const TopicPageEntry: React.FC<TopicPageEntryProps> = ({ topicKey }) => {
  const { title: seoTitle, description: seoDescription } =
    TOPIC_DATA_SEO[topicKey] || {};

  return (
    <Section noBlockPadding>
      <Container>
        <Head
          titleTranslationKey={seoTitle}
          descriptionTranslationKey={seoDescription}
        />
        <ErrorBoundary
          onError={() => (
            <GenericPageError refetch={() => window.location.reload()} />
          )}
        >
          <NoSSRLoggedInSuspense
            fallback={
              <>
                <HeaderNavigation title="" />
                <TopicPageSkeleton />
              </>
            }
          >
            <TopicPageEntryInner topicKey={topicKey} />
          </NoSSRLoggedInSuspense>
        </ErrorBoundary>
        <LoggedOutFooter />
      </Container>
    </Section>
  );
};
